import { useMemo } from 'react';
import styled from 'styled-components';
import { ReactComponent as SupportIcon } from '@elementary/common-ui/assets/24x24/support.svg';
import { ReactComponent as DataPillarWorm } from '@elementary/common-ui/assets/datapillar-worm.svg';
import {
  Breadcrumbs,
  EmptyStateBox,
  Flex,
  FlexColumn,
  FloatingActionsMenu,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import PageOverviewBox from '@elementary/common-ui/components/PageOverviewBox';
import Table, {
  Column,
  ToggleColumnsMenu,
} from '@elementary/common-ui/components/Table';
import { useBreadcrumbs } from '@elementary/common-ui/hooks/useBreadcrumbs';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import {
  distinctArray,
  getModelTitle,
} from '@elementary/common-ui/utils/common';
import { useQuery } from '@tanstack/react-query';
import useReport from 'shared/hooks/useReport';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import { METRICS_TABLE_FILTERS } from '@elementary/common-ui/constants/urlParams';
import { METRICS } from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import * as api from '../api';
import { Metric } from '../types';
import MetricExpandedRow from './MetricExpandedRow';

const METRICS_DOCS_LINK =
  'https://docs.elementary-data.com/cloud/features/anomaly-detection/metrics';

const Wrapper = styled(FlexColumn)`
  height: 100%;
  width: 100%;
  padding: 24px 24px 0 24px;
  position: relative;
`;

const Link = styled.a`
  color: ${({ theme }) => theme.palette.common.black};
`;

const DocumentationLink = styled(Flex)`
  height: 34px;
  background-color: #f0f8ff;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  font-size: 14px;
  padding: 6px 16px;
  border-radius: 8px;
  align-items: center;
`;

const EmptyStateWrapper = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const TableHeader = styled(Flex)`
  justify-content: flex-end;
  padding-bottom: 16px;
`;

const MetricsEmptyState = () => (
  <EmptyStateWrapper>
    <EmptyStateBox title="No data quality metrics" graphic={<DataPillarWorm />}>
      Elementary can calculate and display
      <br />
      data quality metrics on your data assets.
      <br />
      <br />
      <EmptyStateBox.Link href={METRICS_DOCS_LINK} target="_blank">
        Click here to Learn how to add metrics.
      </EmptyStateBox.Link>
    </EmptyStateBox>
  </EmptyStateWrapper>
);

const MetricsExplorer = () => {
  const selectedEnvId = useSelectedEnv();
  const { data: reportData, isFetching } = useReport();

  const { selectedTreeNode, selectedTreeFolderNodes } = useSelectedTreeItem();

  const breadcrumbs = useBreadcrumbs({
    pageTitle: 'Metrics',
  });

  const { data: metricsData, isFetching: isFetchingMetrics } = useQuery(
    [METRICS, selectedEnvId],
    () => api.fetchMetrics(selectedEnvId!),
    {
      enabled: !!selectedEnvId,
    },
  );

  const { treeViewBy } = useTreeViewParams();

  const tableData: Metric[] | undefined = useMemo(() => {
    if (selectedTreeNode?.resourceType === 'folder') {
      const folderMetrics = metricsData?.filter((metric) =>
        selectedTreeFolderNodes?.some((node) =>
          metric.asset_ids.includes(node.id),
        ),
      );
      return folderMetrics;
    }

    if (selectedTreeNode) {
      const selectedNodeMetrics = metricsData?.filter((metric) =>
        metric.asset_ids.includes(selectedTreeNode.id),
      );

      return selectedNodeMetrics;
    }

    return metricsData;
  }, [metricsData, selectedTreeNode, selectedTreeFolderNodes]);

  const selectedDbtAssetNode = useMemo(() => {
    if (
      selectedTreeNode &&
      ['model', 'source'].includes(selectedTreeNode.resourceType || '')
    ) {
      return selectedTreeNode;
    }
    return undefined;
  }, [selectedTreeNode]);

  const selectedDbtAssetData = useMemo(() => {
    if (selectedDbtAssetNode) {
      return reportData?.models?.[selectedDbtAssetNode.id] || undefined;
    }
    return undefined;
  }, [selectedDbtAssetNode, reportData?.models]);

  const selectedTreeFolderTableAssets = useMemo(() => {
    if (selectedTreeNode?.resourceType === 'folder') {
      return selectedTreeFolderNodes?.filter((node) =>
        ['model', 'source', 'table', 'view'].includes(node.resourceType || ''),
      );
    }
    return [];
  }, [selectedTreeFolderNodes, selectedTreeNode]);

  const title = useMemo(() => {
    if (selectedTreeFolderTableAssets?.length) {
      return `${selectedTreeFolderTableAssets?.length} Tables`;
    }

    if (selectedDbtAssetData) {
      return getModelTitle(selectedDbtAssetData);
    }

    if (selectedTreeNode) {
      return selectedTreeNode.id;
    }

    return 'All Tables';
  }, [selectedTreeFolderTableAssets, selectedDbtAssetData, selectedTreeNode]);

  const path = useMemo(() => {
    if (treeViewBy === 'dbt' && selectedTreeNode?.resourceType === 'folder') {
      return selectedTreeNode?.id;
    }

    if (selectedDbtAssetData) {
      return selectedDbtAssetData.full_path || '-';
    }

    if (treeViewBy === 'dbt') {
      return 'dbt_project/';
    }

    return '-';
  }, [selectedDbtAssetData, selectedTreeNode, treeViewBy]);

  const columns: Array<Column<Metric>> = [
    {
      id: 'database_name',
      header: 'Database',
      defaultUntoggled: true,
    },
    { id: 'schema_name', header: 'Schema', defaultUntoggled: true },
    {
      id: 'table_name',
      header: 'Table name',
      sortable: true,
      filterDropdownWidth: 350,
    },
    {
      id: 'column_name',
      header: 'Column name',
      sortable: true,
      filterDropdownWidth: 350,
    },
    {
      id: 'name',
      header: 'Metric name',
      sortable: true,
    },
    {
      id: 'group_by_dimensions',
      header: 'Dimensions',
      sortable: false,
      renderer: (val) => (
        <OverflowTooltip>{val.group_by_dimensions?.join(', ')}</OverflowTooltip>
      ),
      filterOptionsOverride: distinctArray(
        (tableData || []).map((item) => item.group_by_dimensions).flat(),
      ).map((item) => ({ value: item, label: item })),
    },
  ];

  useViewLoadTracking({
    name: 'Metrics Explorer',
    loadDeps: [tableData],
  });

  return (
    <Wrapper>
      <FlexColumn sx={{ mb: 3, gap: 3, width: '100%' }}>
        <Flex
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            justifyItems: 'center',
          }}
        >
          <Breadcrumbs items={breadcrumbs} />
          <DocumentationLink gap={1}>
            <SupportIcon />
            <Link href={METRICS_DOCS_LINK} target="_blank">
              Learn how to add metrics
            </Link>
          </DocumentationLink>
        </Flex>

        <PageOverviewBox
          title={title}
          description={
            title && !!selectedDbtAssetData
              ? selectedDbtAssetData?.description
              : undefined
          }
          path={path}
          owners={
            treeViewBy === 'owners' &&
            selectedTreeNode?.resourceType === 'folder'
              ? [path]
              : selectedDbtAssetData?.owners || []
          }
          tags={
            treeViewBy === 'tags' && selectedTreeNode?.resourceType === 'folder'
              ? [path]
              : selectedDbtAssetData?.tags || []
          }
        />
      </FlexColumn>

      <TableHeader>
        <ToggleColumnsMenu
          columns={columns}
          localStorageKey={`${METRICS_TABLE_FILTERS}_table`}
        />
      </TableHeader>

      <Table
        data={tableData}
        columns={columns}
        idKey="id"
        localStorageKey={`${METRICS_TABLE_FILTERS}_table`}
        urlParamsPrefix={METRICS_TABLE_FILTERS}
        expandedRowRenderer={(rowData) => (
          <MetricExpandedRow metric={rowData} />
        )}
        isLoading={isFetchingMetrics || isFetching}
        emptyStateRenderer={() => <MetricsEmptyState />}
        bottomScrollPadding={100}
      />
      <FloatingActionsMenu />
    </Wrapper>
  );
};

export default MetricsExplorer;
